import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
} from "@mui/material";
import { ActionModal, NoticeboardButton, ACTION_MODAL_FOOTER_PRIMARY_SIZE } from "../../shared";
import type { ChildExtracurricular, ParentChild } from "../../../types/parentFamily";
import { getChildFullName } from "../../../utils/parentFamilyHelpers";
import { FAMILY_OUTLINED_FIELD_SX } from "./familyStyles";

export interface ExtracurricularFormValues {
  id?: string;
  childId?: string;
  name: string;
  schedule: string;
  notes: string;
  amount: string;
  currency: string;
}

interface ExtracurricularFormModalProps {
  open: boolean;
  onClose: () => void;
  initialActivity?: ChildExtracurricular | null;
  childrenOptions?: Pick<ParentChild, "id" | "firstName" | "lastName">[];
  selectedChildId?: string | null;
  onChildChange?: (childId: string) => void;
  /** Receives one entry per selected activity (a single entry when editing). */
  onSave: (values: ExtracurricularFormValues[]) => void;
}

/** Preset activities offered in the dropdown. "Other…" lets staff type a custom one. */
const ACTIVITY_OPTIONS = [
  "Speech and Drama",
  "Ballet",
  "Robotics",
  "Monkeynastix",
  "Swimming",
];

const OTHER_ACTIVITY_VALUE = "__other__";

export const ExtracurricularFormModal: React.FC<ExtracurricularFormModalProps> = ({
  open,
  onClose,
  initialActivity,
  childrenOptions = [],
  selectedChildId,
  onChildChange,
  onSave,
}) => {
  const isEdit = !!initialActivity;
  // Let staff pick which child to link the activities to when adding new ones.
  const showChildPicker = childrenOptions.length >= 1 && !isEdit;

  const [childId, setChildId] = useState<string>(selectedChildId || "");
  // Selected activity values: preset names and/or the "Other" sentinel.
  const [selected, setSelected] = useState<string[]>([]);
  const [customName, setCustomName] = useState("");
  // Cost keyed by activity value (preset name or the "Other" sentinel).
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChildId(selectedChildId || "");
    if (initialActivity) {
      const isPreset = ACTIVITY_OPTIONS.includes(initialActivity.name);
      const value = isPreset ? initialActivity.name : OTHER_ACTIVITY_VALUE;
      setSelected([value]);
      setCustomName(isPreset ? "" : initialActivity.name || "");
      setCosts({
        [value]:
          initialActivity.amount != null ? String(initialActivity.amount) : "",
      });
    } else {
      setSelected([]);
      setCustomName("");
      setCosts({});
    }
    setError(null);
  }, [open, initialActivity, selectedChildId]);

  const displayName = (value: string) =>
    value === OTHER_ACTIVITY_VALUE ? customName.trim() || "Other" : value;

  const handleSubmit = () => {
    if (showChildPicker && !childId) {
      setError("Select which family member these activities are for.");
      return;
    }
    if (selected.length === 0) {
      setError("Select at least one activity.");
      return;
    }
    if (selected.includes(OTHER_ACTIVITY_VALUE) && !customName.trim()) {
      setError("Enter the activity name for 'Other'.");
      return;
    }
    for (const value of selected) {
      const cost = (costs[value] || "").trim();
      if (cost) {
        const amount = Number.parseFloat(cost);
        if (!Number.isFinite(amount) || amount < 0) {
          setError(`Enter a valid cost for ${displayName(value)}, or leave it blank.`);
          return;
        }
      }
    }

    const values: ExtracurricularFormValues[] = selected.map((value) => ({
      id: isEdit ? initialActivity!.id : undefined,
      childId: showChildPicker ? childId : selectedChildId || childId || undefined,
      name: displayName(value),
      schedule: isEdit ? initialActivity!.schedule || "" : "",
      notes: isEdit ? initialActivity!.notes || "" : "",
      amount: (costs[value] || "").trim(),
      currency: isEdit ? initialActivity!.currency || "ZAR" : "ZAR",
    }));

    onSave(values);
    onClose();
  };

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit activity" : "Add Extracurricular"}
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <NoticeboardButton variant="secondary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={onClose}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton variant="primary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={handleSubmit}>
            Save
          </NoticeboardButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ...FAMILY_OUTLINED_FIELD_SX }}>
        {showChildPicker ? (
          <FormControl fullWidth size="small" required>
            <InputLabel>Family member</InputLabel>
            <Select
              label="Family member"
              value={childId}
              onChange={(e) => {
                const id = e.target.value;
                setChildId(id);
                onChildChange?.(id);
              }}
            >
              {childrenOptions.map((child) => (
                <MenuItem key={child.id} value={child.id}>
                  {getChildFullName(child)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        {isEdit ? (
          <FormControl fullWidth size="small" required>
            <InputLabel>Activity</InputLabel>
            <Select
              label="Activity"
              value={selected[0] || ""}
              onChange={(e) => {
                const value = e.target.value;
                setSelected(value ? [value] : []);
                if (value !== OTHER_ACTIVITY_VALUE) setCustomName("");
              }}
            >
              {ACTIVITY_OPTIONS.map((activity) => (
                <MenuItem key={activity} value={activity}>
                  {activity}
                </MenuItem>
              ))}
              <MenuItem value={OTHER_ACTIVITY_VALUE}>Other…</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <FormControl fullWidth size="small" required>
            <InputLabel>Activities</InputLabel>
            <Select
              label="Activities"
              multiple
              value={selected}
              onChange={(e) => setSelected(e.target.value as string[])}
              renderValue={(sel) =>
                (sel as string[]).map((value) => displayName(value)).join(", ")
              }
            >
              {ACTIVITY_OPTIONS.map((activity) => (
                <MenuItem key={activity} value={activity}>
                  <Checkbox checked={selected.includes(activity)} />
                  <ListItemText primary={activity} />
                </MenuItem>
              ))}
              <MenuItem value={OTHER_ACTIVITY_VALUE}>
                <Checkbox checked={selected.includes(OTHER_ACTIVITY_VALUE)} />
                <ListItemText primary="Other…" />
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {selected.includes(OTHER_ACTIVITY_VALUE) ? (
          <TextField
            label="Other activity name"
            required
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="e.g. Chess club"
            fullWidth
            size="small"
            autoFocus
          />
        ) : null}

        {selected.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {selected.length > 1 ? (
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                Cost per activity (optional)
              </Typography>
            ) : null}
            {selected.map((value) => (
              <TextField
                key={value}
                label={
                  selected.length > 1
                    ? `${displayName(value)} — cost`
                    : "Cost (optional)"
                }
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={costs[value] || ""}
                onChange={(e) =>
                  setCosts((c) => ({ ...c, [value]: e.target.value }))
                }
                placeholder="e.g. 450"
                fullWidth
                size="small"
                helperText="Adds a matching line on the family member's fee statement"
              />
            ))}
          </Box>
        ) : null}

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
      </Box>
    </ActionModal>
  );
};
